import { useMemo } from 'react';

import { TaxonomyType } from '@revelio/data-access';

import type { CustomSkillTaxonomySelection } from '../../../next/CustomDash/filters/definitions/skillTaxonomy';
import { useTaxonomyList } from './useTaxonomyList';
import { useTaxonomyLatestVersion } from './useTaxonomyVersions';

type SkillTaxonomy = {
  id: number;
  name: string;
  version: string;
  is_latest: boolean;
};

export const useSkillTaxonomyVersions = () => {
  const {
    taxonomies: referenceTaxonomies,
    hasData: hasReferenceTaxonomies,
    fetching: referenceFetching,
    error: referenceError,
  } = useTaxonomyList({
    taxonomyType: TaxonomyType.Skill,
  });
  const {
    version: latestReferenceVersion,
    hasData: hasLatestReferenceVersion,
    fetching: latestReferenceFetching,
    error: latestReferenceError,
  } = useTaxonomyLatestVersion({
    taxonomyType: TaxonomyType.Skill,
  });

  const mappedReferenceTaxonomies = useMemo(() => {
    if (!hasReferenceTaxonomies || !hasLatestReferenceVersion) {
      return undefined;
    }

    return referenceTaxonomies.map(
      (taxonomy): SkillTaxonomy => ({
        id: taxonomy.id,
        name: taxonomy.name,
        version: taxonomy.version,
        is_latest:
          latestReferenceVersion !== undefined &&
          latestReferenceVersion !== null &&
          taxonomy.version === latestReferenceVersion,
      })
    );
  }, [
    hasLatestReferenceVersion,
    hasReferenceTaxonomies,
    latestReferenceVersion,
    referenceTaxonomies,
  ]);

  return {
    taxonomies: mappedReferenceTaxonomies,
    loading:
      referenceFetching ||
      latestReferenceFetching ||
      (!hasReferenceTaxonomies && !referenceError) ||
      (!hasLatestReferenceVersion && !latestReferenceError),
    error: referenceError ?? latestReferenceError,
  };
};

export const useCurrentSkillTaxonomyVersion = () => {
  const { taxonomies, loading, error } = useSkillTaxonomyVersions();

  const defaultVersion = (() => {
    const latestDefaultTaxonomy = taxonomies?.find(
      (taxonomy: SkillTaxonomy) => taxonomy.is_latest
    );
    if (latestDefaultTaxonomy) return latestDefaultTaxonomy.version;

    return undefined;
  })();

  const getSpecificTaxonomyVersion = (
    taxonomySelection: CustomSkillTaxonomySelection
  ) => {
    if (taxonomySelection.taxonomyId) {
      const taxonomy = taxonomies?.find(
        (taxonomy: SkillTaxonomy) =>
          taxonomy.id === taxonomySelection.taxonomyId
      );
      return taxonomy?.version;
    }

    return defaultVersion;
  };

  return {
    version: defaultVersion,
    taxonomies,
    loading: loading || (!taxonomies && !error),
    error,
    isValidTaxonomyId: (taxonomyId: number) => {
      if (error) {
        console.error('Taxonomy versions are unknown', error);
        return false;
      }
      return (
        taxonomies?.some(
          (taxonomy: SkillTaxonomy) => taxonomy.id === taxonomyId
        ) ?? false
      );
    },
    getSpecificTaxonomyVersion,
  };
};
